import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import {
  AnyObject, AuthCache,
  ErrorMessage,
  FormCurrentData,
  FormDataSetConfig,
  FormFieldConfig,
  FormFieldType,
  ResponseSimple,
  ValueType
} from '@mri/types';
import {filter, forkJoin, map, mergeMap, Observable, of, Subject, Subscription, tap} from 'rxjs';
import {AuthResourceService, GenericResourceService} from '@mri/app/resources';
import {environment} from '@mri/environment';
import {toBoolean, unFlattenObject} from '@mri/functions';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {FieldImageComponent} from '../inputs';
import {catchError} from 'rxjs/operators';

@Component({
  selector: 'app-form-data-set',
  templateUrl: './form-data-set.component.html',
  styleUrl: './form-data-set.component.scss'
})
export class FormDataSetComponent implements OnInit, OnDestroy {

  private _elementId$ = new Subject<string>();
  private _subscriptions = new Subscription();
  private _elementId?: string;
  private _element = {};
  private _auth?: AuthCache;

  valueRecord: Record<string, ValueType | Array<ValueType>> = {};
  errors: Array<ErrorMessage> = [];
  isSubmitting = false;

  @Input() config: FormDataSetConfig;

  @Input() set element(element: AnyObject) {
    this._element = {...element};
    this._fillFields();
  }

  @Input() set elementId(id: string) {
    this._elementId = id;
    const {gatewayUrl} = this.config || {};
    if (!gatewayUrl) return;

    this._elementId$.next(id);
  }

  @Output() submitted = new EventEmitter<Record<string, ValueType | Array<ValueType>>>();
  @Output() submittedResponse = new EventEmitter<AnyObject>();

  @ViewChildren(FieldImageComponent) fieldImages: QueryList<FieldImageComponent>;

  get element(): AnyObject {
    return this._element;
  }

  get elementId(): string {
    return this._elementId || '';
  }

  get fields(): FormFieldConfig[] {
    return this.config.fields;
  }

  get values(): Record<string, AnyObject> {
    return this.valueRecord;
  }

  get currentData(): FormCurrentData {
    return {
      values: this.values,
      auth: this._auth
    }
  }

  get gateway(): string | undefined {
    const {gatewayParams} = this.config;

    if (!gatewayParams) return this.config.gatewayUrl;

    return gatewayParams.reduce((acc, param) => {
      const value = this._route.snapshot.paramMap.get(param)

      acc = acc.replaceAll(`:${param}`, value || '');

      return acc;
    }, this.config.gatewayUrl || '');
  }

  constructor(
    private _genericSrv: GenericResourceService,
    private _authSrcSrv: AuthResourceService,
    private _route: ActivatedRoute,
    private _location: Location,
    private _router: Router,
    private _changeDetection: ChangeDetectorRef
  ) {
    this._subscriptions.add(
      this._elementId$
        .pipe(
          filter(id => this.gateway !== undefined && !!id),
          mergeMap(id => this._genericSrv.get<ResponseSimple<AnyObject>>(`${environment.apiUrl}/${this.gateway}/${id}`)),
          tap(response => this._element = response.object),
          tap(() => this._fillFields())
        )
        .subscribe()
    );
  }

  ngOnInit(): void {

    this._subscriptions.add(
      this._authSrcSrv.authFirst$
        .pipe(
          tap(auth => this._auth = auth),
          tap(() => this._elementId$.next(this._elementId || '')),
          tap(() => this._fillFields())

        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  getElementResult<T = AnyObject>(): T {
    const {handleObject} = this.config;
    const values = this.valueRecord;

    if (typeof handleObject !== 'function') return unFlattenObject(values as AnyObject);

    return handleObject(values) as AnyObject;
  }

  onSubmit(redirectTo?: string) {
    this.isSubmitting = true;

    const uploads$ = this.fieldImages.length ? this.fieldImages.map(fieldImage => fieldImage.uploadSource()) : [of(null)];

    this._subscriptions.add(
      forkJoin(uploads$)
        .pipe(
          mergeMap(() => this._sendData()),
          /*tap(response => {
            if (!response?.message) return;

            this._snackBar.open(response.message, '', {
              panelClass: ['bg-success-500'],
              horizontalPosition: 'end',
              verticalPosition: 'top',
              duration: 5000
            });
          }),*/
          tap((response) => {
            if (redirectTo === 'list' && this.config.listRedirectUrl) this._router.navigate([this.config.listRedirectUrl]);
            if (redirectTo === 'back' || !this._elementId) this._location.back();

            this.submitted.next(this.getElementResult());
            this.submittedResponse.next(response);
          }),
          catchError(err => {
            this._showAlerts(err);
            return of({});
          }),
          tap(() => this.isSubmitting = false)
        ).subscribe()
    );
  }

  setValue(key: string, value: AnyObject) {
    this.valueRecord[key] = value;
    this._changeDetection.markForCheck();
  }


  private _fillFields() {
    const valueRecord: Record<string, ValueType | ValueType[]> = {};

    this.fields
      .map(field => this._fillField(field, this._element || {}))
      .forEach(valueMap => Object.assign(valueRecord, valueMap));

    this.valueRecord = valueRecord;
  }

  private _showAlerts(message: string | string[]) {
    if (!message) return;
    const messages = Array.isArray(message) ? message : [message];

    this.errors = (messages || []).map(messageDescription => {
      const [description, type] = messageDescription.split('|');

      const [fieldKey, ...content] = description.split(' ');

      return {
        type: type?.trim() || 'danger',
        message: content.join(' ').trim(),
        fieldKey: fieldKey.trim()
      };
    });
  }

  private _fillField(field: FormFieldConfig, element?: AnyObject): Record<string, AnyObject> {
    const {fillValue, fillValues} = field;

    if (typeof fillValue === 'function') return this._fillWithFunctionValue(field, element);
    if (typeof fillValues === 'function') return this._fillWithFunctionValues(field, element);

    if (field.type == FormFieldType.DATE_RANGE) return this._fillDateRange(field, element);
    return this._fillGeneric(field, element);
  }

  private _fillWithFunctionValue(field: FormFieldConfig, element: AnyObject): Record<string, AnyObject> {
    const {fillValue, fillDefaultValue, key} = field;

    if (typeof fillValue === 'function') {
      const value = fillValue(element);
      if (value !== undefined) return {[key || '']: value};
    }

    if (typeof fillDefaultValue === 'function') {
      const value = fillDefaultValue();
      if (value !== undefined) return {[key || '']: value};
    }

    return {[key || '']: undefined};
  }

  private _fillWithFunctionValues(field: FormFieldConfig, element?: AnyObject): Record<string, AnyObject> {
    const {fillValues, fillDefaultValues, keys} = field;

    if (typeof fillValues === 'function') {
      const values = fillValues(element);
      if (values !== undefined) return {[(keys || [])[0] || '']: values[0], [(keys || [])[1] || '']: values[1]};
    }

    if (typeof fillDefaultValues === 'function') {
      const values = fillDefaultValues();
      if (values !== undefined) return {[(keys || [])[0] || '']: values[0], [(keys || [])[1] || '']: values[1]};
    }

    return {[(keys || [])[0] || '']: undefined, [(keys || [])[1] || '']: undefined};
  }

  private _fillGeneric(field: FormFieldConfig, element: AnyObject): Record<string, AnyObject> {
    const {fillDefaultValue, key} = field;

    const value = element[key || ''];
    if (value !== undefined) return {[key || '']: value};

    if (typeof fillDefaultValue === 'function') {
      const value = fillDefaultValue();
      if (value !== undefined) return {[key || '']: value};
    }

    return {[key || '']: undefined};
  }

  private _fillDateRange(field: FormFieldConfig, element: AnyObject): Record<string, AnyObject> {
    const {fillDefaultValues, keys} = field;

    const values = [element[(keys || [])[0] || ''], element[(keys || [])[1] || '']];
    if (values[0] !== undefined) return {[(keys || [])[0] || '']: values[0], [(keys || [])[1] || '']: values[1]};

    if (typeof fillDefaultValues === 'function') {
      const values = fillDefaultValues();
      if (values !== undefined) return {[(keys || [])[0] || '']: values[0], [(keys || [])[1] || '']: values[1]};
    }

    return {[(keys || [])[0] || '']: undefined, [(keys || [])[1] || '']: undefined};
  }

  private _sendData(): Observable<ResponseSimple<AnyObject> | null> {
    const object = this.getElementResult()

    const gatewayUrl = this.gateway;
    if (!gatewayUrl) return of(null);

    if (this._elementId)
      return this._genericSrv.put<ResponseSimple<AnyObject>>(`${environment.apiUrl}/${gatewayUrl}/${this._elementId}`, object);

    return this._genericSrv.post<ResponseSimple<AnyObject>>(`${environment.apiUrl}/${gatewayUrl}`, object);
  }



  protected readonly FormFieldType = FormFieldType;
  protected readonly toBoolean = toBoolean;

}
